/**
 * Pro-ration helpers for the Claim Allowables table (CLE-2360).
 *
 * A single CPT can appear on multiple remit lines (e.g. a bilateral pair, or a
 * units=1 line plus a units=266 line). The precalc models the CPT as ONE
 * provider procedure with a single `finalAmountInCents`, so that expected total
 * must be split across the underlying lines for the per-row Expected column.
 */

export interface IProrationLine {
  cptCode: string;
  billedUnits?: number | null;
  quantity?: number | null;
}

/**
 * Per-line weight used to pro-rate a CPT's expected allowable across the
 * (possibly multiple) remit lines that carry the same code. Prefer the line's
 * `billedUnits`; fall back to `quantity`, then to 1.
 *
 * CLE-2360: the prior logic mixed sources — it weighted the numerator by
 * `max(billedUnits, quantity)` while dividing by the calculatedData CPT
 * `quantity`. A bilateral line (modifier 50) carries `quantity: 2` but
 * `billedUnits: 1`, so it was weighted as 2 units against a denominator of 2,
 * grabbing the FULL cpt total while the second line still took half — together
 * overstating the CPT's expected (e.g. $317.62 + $158.81 instead of $317.62).
 * Weighting by `billedUnits` keeps the bilateral pair at one unit each.
 */
export const lineProrationWeight = (li: IProrationLine): number =>
  Number(li?.billedUnits) || Number(li?.quantity) || 1;

/**
 * Pro-rate each CPT's expected allowable (the providerProcedure
 * `finalAmountInCents`) across its underlying remit lines, weighted by
 * {@link lineProrationWeight}. Returns one value per input line (indexed
 * identically), or `null` for lines whose CPT has no matching expected.
 *
 * The last line of each CPT group absorbs the rounding remainder so the
 * per-line expecteds sum EXACTLY to the CPT's `finalAmountInCents` — keeping
 * the Claim Allowables rows reconciled with the claim-level subtotal.
 */
export function prorateExpectedByCpt(
  lineItems: IProrationLine[],
  finalAmountInCentsForCpt: (cptCode: string) => number | null | undefined
): Array<number | null> {
  const result: Array<number | null> = new Array(lineItems.length).fill(null);
  const groups = new Map<string, number[]>();
  lineItems.forEach((li, idx) => {
    const existing = groups.get(li.cptCode);
    if (existing) existing.push(idx);
    else groups.set(li.cptCode, [idx]);
  });
  groups.forEach((indices, cptCode) => {
    const finalAmount = finalAmountInCentsForCpt(cptCode);
    if (finalAmount == null) return; // leave nulls for unmatched CPTs
    const weightSum = indices.reduce(
      (sum, idx) => sum + lineProrationWeight(lineItems[idx]),
      0
    );
    let allocated = 0;
    indices.forEach((idx, i) => {
      const isLast = i === indices.length - 1;
      const expected = isLast
        ? finalAmount - allocated
        : Math.round(
            (finalAmount * lineProrationWeight(lineItems[idx])) / weightSum
          );
      allocated += expected;
      result[idx] = expected;
    });
  });
  return result;
}
